import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading, useDocTitle } from "../components/Reveal";
import { MediaImg } from "../components/MediaImg";
import { CTABanner } from "../components/CTABanner";
import { GoldLink } from "../components/Buttons";
import { Marquee } from "../components/Marquee";
import { IMAGES } from "../data/site";

const OFFERINGS = [
  {
    title: "Commercial & Brand Films",
    copy: "The spot your sales team opens with. Concept, script, shoot and edit — built around the one thing your customers need to believe.",
    image: IMAGES.heroRig,
  },
  {
    title: "Social & Short-Form",
    copy: "Reels, vertical video and a posting rhythm that keeps you visible between campaigns — shot in batches, delivered ready to post.",
    image: IMAGES.social,
  },
  {
    title: "Photography & Product",
    copy: "People, products and places — photo libraries with one consistent standard across your website, social, and print.",
    image: IMAGES.product,
  },
  {
    title: "Drone & Post-Production",
    copy: "Licensed aerials plus editing, color, sound and motion graphics. Polish is included, not priced separately.",
    image: IMAGES.drone,
  },
];

const TIERS = [
  {
    name: "Starter Crew",
    blurb: "For teams getting consistent for the first time.",
    features: ["1 shoot day / month", "8–10 finished assets", "Social-ready cutdowns", "Quarterly content planning", "48-hour first edits"],
    featured: false,
    testId: "tier-starter",
  },
  {
    name: "Growth Engine",
    blurb: "For marketing teams with real targets to hit.",
    features: ["2 shoot days / month", "20+ finished assets", "Brand film refresh yearly", "Dedicated producer", "Paid-media versions", "Priority scheduling"],
    featured: true,
    testId: "tier-growth",
  },
  {
    name: "Enterprise Production",
    blurb: "For organizations treating video as infrastructure.",
    features: ["Embedded crew days", "Multi-location coverage", "Internal comms & training", "Executive content", "Asset library management", "Quarterly strategy reviews"],
    featured: false,
    testId: "tier-enterprise",
  },
];

export default function ForBusinesses() {
  useDocTitle("For Businesses");
  return (
    <div data-testid="businesses-page">
      <PageHero
        eyebrow="Video Production & Content"
        lines={[<>We don't just shoot it.</>, <span key="g" className="text-gold-dark">We help figure out what's worth shooting.</span>]}
        sub="Commercial video, brand films, social content and photography for Wisconsin businesses — planned around outcomes, not equipment lists."
      >
        <div className="mt-9 flex flex-wrap gap-4">
          <GoldLink to="/start-a-project" testId="biz-hero-cta">Plan a Shoot</GoldLink>
        </div>
      </PageHero>

      <Marquee items={["Commercials", "Brand Films", "Social Content", "Interviews", "Photography", "Drone", "Post-Production"]} />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
        <SectionHeading
          eyebrow="What We Make"
          title={<>One partner for <span className="text-gold-dark">every frame.</span></>}
          sub="No handoffs between freelancers, no gear talk. Just work that shows up on time and does its job."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {OFFERINGS.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.07}>
              <div className="group grid overflow-hidden border border-ink/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:grid-cols-5">
                <div className="overflow-hidden sm:col-span-2">
                  <MediaImg src={o.image} alt={o.title} className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-full" />
                </div>
                <div className="p-7 sm:col-span-3">
                  <h3 className="font-display text-xl font-bold tracking-tight text-ink">{o.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-smoke">{o.copy}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
          <SectionHeading
            eyebrow="Monthly Content"
            title={<>An always-on content engine, <span className="text-gold-dark">without the payroll.</span></>}
            sub="Retainers built for Wisconsin marketing teams. Every plan starts with a strategy call — pricing follows scope."
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {TIERS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <div
                  data-testid={t.testId}
                  className={`flex h-full flex-col p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl ${
                    t.featured ? "border-2 border-gold bg-night text-sand" : "border border-ink/10 bg-white"
                  }`}
                >
                  {t.featured && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Most Popular</p>
                  )}
                  <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">{t.name}</h3>
                  <p className={`mt-2 font-serif text-lg italic ${t.featured ? "text-sand/60" : "text-smoke"}`}>
                    {t.blurb}
                  </p>
                  <ul className="mt-7 flex-1 space-y-3.5">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <Check size={16} className={`mt-0.5 shrink-0 ${t.featured ? "text-gold" : "text-gold-dark"}`} />
                        <span className={t.featured ? "text-sand/80" : "text-smoke"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/start-a-project"
                    data-testid={`${t.testId}-cta`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 font-display text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                      t.featured
                        ? "bg-gold text-ink hover:bg-sand"
                        : "border border-ink/20 text-ink hover:border-gold hover:text-gold-dark"
                    }`}
                  >
                    Get a Quote <ArrowUpRight size={14} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40">
              Every retainer is scoped to your goals — no generic packages, no surprise line items
            </p>
          </Reveal>
        </div>
      </section>

      <CTABanner
        title="Build the brand. Tell the story. Get it seen."
        sub="One conversation is all it takes to map your next quarter of content."
      />
    </div>
  );
}
